import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from './Button';
import { Icon } from './Icon';

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'sm',
  closeButton,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'lg';
  /** The X in the corner, for a dialog with no footer button to leave by. */
  closeButton?: boolean;
}) {
  const width = size === 'lg' ? 'w-[min(94vw,40rem)]' : 'w-[min(92vw,30rem)]';
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px]" />
        <DialogPrimitive.Content
          className={`fixed left-1/2 top-1/2 z-50 ${width} max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md border border-line bg-surface p-5 shadow-raised focus:outline-none`}
        >
          {closeButton && (
            <DialogPrimitive.Close
              aria-label="Close"
              className="absolute right-3 top-3 rounded-tight p-1 text-ink-muted transition-colors hover:bg-sunken hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <Icon name="close" className="block text-[20px]" />
            </DialogPrimitive.Close>
          )}
          <DialogPrimitive.Title className="text-lg font-semibold text-ink">
            {title}
          </DialogPrimitive.Title>
          {description && (
            <DialogPrimitive.Description className="mt-1.5 text-sm text-ink-muted">
              {description}
            </DialogPrimitive.Description>
          )}
          {children && <div className="mt-4">{children}</div>}
          {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * The confirm-before-destructive variant `phase-0-foundation.md` task 3
 * asks Dialog.tsx to support "distinctly" from the standard `Modal` above —
 * before Stage 4, every destructive action (e.g. `DeactivateEmployeeDialog`)
 * just used `Modal` with a `danger`-colored button, with no structural
 * difference from a standard dialog. New; not wired into any existing
 * destructive dialog in Phase 0 — that migration is each dialog's own
 * page-level redesign phase.
 *
 * Deliberately presentational/controlled, matching `Modal`'s own style: a
 * typed-confirmation input (if the caller wants one) lives in `children`,
 * with its match-check owned by the caller via `confirmDisabled` — this
 * component doesn't invent its own form state.
 */
export function DestructiveConfirmDialog({
  open,
  onOpenChange,
  title,
  subject,
  consequence,
  confirmLabel,
  onConfirm,
  confirmDisabled,
  loading,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subject?: ReactNode;
  consequence: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px]" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,30rem)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-card border border-line bg-surface shadow-raised focus:outline-none">
          <div className="flex items-start gap-3 rounded-t-card border-b border-line bg-alert-soft/30 p-5">
            <div className="rounded bg-alert-soft p-2 text-alert">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <DialogPrimitive.Title className="text-base font-semibold text-alert">{title}</DialogPrimitive.Title>
              {subject && <p className="mt-0.5 text-xs text-ink-muted">{subject}</p>}
            </div>
          </div>
          <div className="space-y-3.5 p-5 text-xs">
            <div className="space-y-1 rounded border border-alert/25 bg-alert-soft p-3 text-alert">
              <p className="font-semibold">Irreversible action consequence:</p>
              <p className="leading-relaxed text-[11px]">{consequence}</p>
            </div>
            {children}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-line bg-sunken/40 p-4">
            <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" loading={loading} disabled={confirmDisabled} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
